import { useTranslation } from 'react-i18next';

import { VoiceMeetingCaptionsContainer } from '../VoiceMeetingCaptions';

import { CloseIcon } from 'assets/svg';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useTrackedInteractionAction } from 'features/analytics/hooks';
import {
  clearCaptions,
  selectCaptionsEnabled,
  toggleCaptionsThunk,
} from 'features/call/call-captions/store';
import styled from 'features/theme/styled';

interface VrsCallCaptionsRegionProps {
  holdRegionOpen?: boolean;
  onPrunedToEmpty?: () => void;
  onRegionDismiss?: () => void;
}

export const VrsCallCaptionsRegion = ({
  holdRegionOpen = false,
  onPrunedToEmpty,
  onRegionDismiss,
}: VrsCallCaptionsRegionProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const captionsEnabled = useAppSelector(selectCaptionsEnabled);

  const { onActionClick } = useTrackedInteractionAction({
    actionName: 'captions-close',
    methodName: 'handleCaptionsClose',
  });

  const handleClose = () => {
    if (captionsEnabled) {
      dispatch(toggleCaptionsThunk());
    } else {
      dispatch(clearCaptions());
    }
    onRegionDismiss?.();
    onActionClick();
  };

  return (
    <CaptionsRegion data-testid='vrs-call-captions-region'>
      <VoiceMeetingCaptionsContainer
        holdRegionOpen={holdRegionOpen}
        onPrunedToEmpty={onPrunedToEmpty}
      />
      <CloseButton
        type='button'
        aria-label={t('closeCaptions')}
        data-testid='vrs-call-captions-close-button'
        onClick={handleClose}
      >
        <CloseIcon />
      </CloseButton>
    </CaptionsRegion>
  );
};

const CaptionsRegion = styled('div')`
  position: relative;
  width: 100%;
  flex: 0 0 22%;
  min-height: 0;
  padding: ${({ theme }) => theme.spacing(2, 0, 3)};
  background-color: ${({ theme }) => theme.palette.grey[900]};
`;

const CloseButton = styled('button')`
  position: absolute;
  top: 15px;
  right: 25px;
  z-index: 1;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.grey[300]};

  &:hover {
    color: ${({ theme }) => theme.palette.grey[100]};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
